import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  NotePencil, X, FloppyDisk, Trash, ClockCounterClockwise, Check, CloudArrowUp, WifiSlash,
} from '@phosphor-icons/react';
import axios from 'axios';
import { Button } from '../ui/button';
import { useAuth } from '../../contexts/AuthContext';
import { useCompany } from '../../contexts/CompanyContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEBOUNCE_MS = 700;

/**
 * QuickNotesModal — personal scratchpad always reachable from the topbar.
 *
 *   - **Server-synced**: stored in MongoDB via /api/quick-notes (one doc per
 *     user+company).  Notes follow the user across devices and browsers.
 *   - **Optimistic with offline fallback**: writes are debounced (~700ms) and
 *     mirrored to localStorage.  If the network drops, the user can keep
 *     typing — the next successful save flushes the pending text to the
 *     server, and the UI shows a clear "offline · saved locally" pill.
 *   - Per-user-per-tenant key for the localStorage cache.
 */
export default function QuickNotesModal({ open, onClose }) {
  const { user } = useAuth();
  const { company } = useCompany();

  // Per-user-per-tenant offline cache key
  const storageKey = useMemo(() => {
    if (!user?.id) return null;
    const cid = company?.id || 'no-company';
    return `bizcore.quick_notes.${cid}.${user.id}`;
  }, [user?.id, company?.id]);

  const [text, setText] = useState('');
  const [savedAt, setSavedAt] = useState(null);
  // status: idle | hydrating | saving | saved | offline | error
  const [status, setStatus] = useState('idle');
  const debounceRef = useRef(null);
  const textareaRef = useRef(null);
  // Refs to avoid stale closures when the user closes the modal mid-debounce
  const lastSavedTextRef = useRef('');
  const isMountedRef = useRef(false);

  // ---- Cache helpers ----
  const readCache = () => {
    if (!storageKey) return null;
    try {
      const raw = localStorage.getItem(storageKey);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  };
  const writeCache = (t, savedAtIso) => {
    if (!storageKey) return;
    try {
      localStorage.setItem(storageKey, JSON.stringify({ text: t, savedAt: savedAtIso }));
    } catch { /* quota or disabled */ }
  };

  // ---- Hydrate when opened ----
  useEffect(() => {
    if (!open || !storageKey) return;
    isMountedRef.current = true;
    const cached = readCache();
    if (cached) {
      setText(cached.text || '');
      setSavedAt(cached.savedAt || null);
      lastSavedTextRef.current = cached.text || '';
    } else {
      setText('');
      setSavedAt(null);
      lastSavedTextRef.current = '';
    }
    setStatus('hydrating');
    (async () => {
      try {
        const res = await axios.get(`${API}/quick-notes`);
        if (!isMountedRef.current) return;
        const serverText = res.data?.text || '';
        const serverSavedAt = res.data?.updated_at || null;
        // If server has newer / longer text, prefer server; otherwise keep cache
        // (covers the case where user typed offline before opening on a 2nd device).
        if (serverSavedAt && (!cached?.savedAt || serverSavedAt >= cached.savedAt)) {
          setText(serverText);
          setSavedAt(serverSavedAt);
          lastSavedTextRef.current = serverText;
          writeCache(serverText, serverSavedAt);
        } else if (cached?.text && cached.text !== serverText) {
          // Cache is newer — push it back to the server
          await pushToServer(cached.text);
        }
        setStatus('idle');
      } catch (e) {
        // 401 -> user logged out; otherwise treat as offline
        setStatus(e?.response?.status === 401 ? 'error' : 'offline');
      }
    })();
    setTimeout(() => textareaRef.current?.focus(), 30);
    return () => { isMountedRef.current = false; };
  }, [open, storageKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const pushToServer = async (t) => {
    setStatus('saving');
    try {
      const res = await axios.put(`${API}/quick-notes`, { text: t });
      const stamp = res.data?.updated_at || new Date().toISOString();
      setSavedAt(stamp);
      lastSavedTextRef.current = t;
      writeCache(t, stamp);
      setStatus('saved');
    } catch {
      // Stash locally and surface offline state
      const stamp = new Date().toISOString();
      writeCache(t, stamp);
      setStatus('offline');
    }
  };

  // ---- Debounced auto-save on text change ----
  useEffect(() => {
    if (!open || !storageKey || status === 'hydrating') return;
    if (text === lastSavedTextRef.current) return; // no-op
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setStatus(s => (s === 'offline' ? 'offline' : 'saving'));
    debounceRef.current = setTimeout(() => { pushToServer(text); }, DEBOUNCE_MS);
    return () => clearTimeout(debounceRef.current);
  }, [text, open, storageKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset the "saved" pip after a moment
  useEffect(() => {
    if (status !== 'saved') return;
    const t = setTimeout(() => setStatus('idle'), 1200);
    return () => clearTimeout(t);
  }, [status]);

  const handleClear = () => {
    if (!window.confirm('Clear all notes? This cannot be undone.')) return;
    setText('');
    pushToServer('');
  };

  const handleCopy = async () => {
    if (!text) return;
    try { await navigator.clipboard.writeText(text); setStatus('saved'); }
    catch { /* clipboard blocked */ }
  };

  const lastSavedLabel = useMemo(() => {
    if (!savedAt) return 'Not saved yet';
    try {
      const d = new Date(savedAt);
      return `Saved ${d.toLocaleString()}`;
    } catch { return 'Saved'; }
  }, [savedAt]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4"
      onClick={onClose}
      data-testid="quick-notes-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-xl bg-card text-card-foreground rounded-xl border shadow-2xl overflow-hidden flex flex-col"
        style={{ maxHeight: '85vh', minHeight: '420px' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b bg-amber-50 dark:bg-amber-950/30">
          <div className="flex items-center gap-2">
            <NotePencil size={18} weight="fill" className="text-amber-600 dark:text-amber-400" />
            <span className="font-semibold">Quick Notes</span>
            <span className="text-xs text-muted-foreground hidden sm:inline">
              · synced to your account
            </span>
          </div>
          <button
            onClick={onClose}
            aria-label="Close quick notes"
            className="rounded-md p-1.5 hover:bg-black/5 dark:hover:bg-white/10 text-muted-foreground"
            data-testid="quick-notes-close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Textarea */}
        <textarea
          ref={textareaRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={
            "Jot anything here — TODOs, customer call-back numbers, quick math, freight calculations, customer codes…\n\n" +
            "Auto-syncs to your account so it's available on every device you sign in to."
          }
          className="flex-1 w-full p-4 bg-transparent text-sm font-mono leading-relaxed resize-none outline-none focus:outline-none placeholder:text-muted-foreground/60"
          data-testid="quick-notes-textarea"
          spellCheck={true}
        />

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 px-3 py-2 border-t bg-muted/30 text-xs">
          <div className="flex items-center gap-2 text-muted-foreground">
            {status === 'hydrating' && (
              <>
                <ClockCounterClockwise size={12} className="animate-spin" /> loading…
              </>
            )}
            {status === 'saving' && (
              <>
                <CloudArrowUp size={12} className="animate-pulse text-sky-500" /> syncing…
              </>
            )}
            {status === 'saved' && (
              <>
                <Check size={12} className="text-emerald-500" /> synced
              </>
            )}
            {status === 'offline' && (
              <>
                <WifiSlash size={12} className="text-amber-500" />{' '}
                <span className="text-amber-600 dark:text-amber-400">
                  offline · saved locally — will sync when reconnected
                </span>
              </>
            )}
            {status === 'error' && (
              <span className="text-rose-500">sync error — please re-open</span>
            )}
            {status === 'idle' && (
              <span data-testid="quick-notes-last-saved">{lastSavedLabel}</span>
            )}
            <span className="opacity-60">
              · {text.length.toLocaleString()} char{text.length === 1 ? '' : 's'}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={handleCopy}
              className="h-7 px-2 text-xs"
              disabled={!text}
              data-testid="quick-notes-copy"
              title="Copy all to clipboard"
            >
              <FloppyDisk size={12} className="mr-1" /> Copy
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={handleClear}
              className="h-7 px-2 text-xs text-destructive hover:text-destructive"
              disabled={!text}
              data-testid="quick-notes-clear"
              title="Clear all notes"
            >
              <Trash size={12} className="mr-1" /> Clear
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
